import React, { useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { GameBoard } from './games/GameBoard';
import { GameHeader } from './game/GameHeader';
import { GameSelection } from './game/GameSelection';
import { BettingPanel } from './game/BettingPanel';
import { GameResult } from './game/GameResult';
import { LiveGameHistory } from './game/LiveGameHistory';
import { games } from '../data/games';
import { usePoints } from '../hooks/usePoints';
import { useGameHistory } from '../hooks/useGameHistory';
import type { User } from '../types/user';
import type { GameType } from '../types/games';

interface GameInterfaceProps {
  onBack: () => void;
  user: User | null;
}

export function GameInterface({ onBack, user }: GameInterfaceProps) {
  const [selectedGame, setSelectedGame] = useState<number | null>(null);
  const [currentBet, setCurrentBet] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [gameResult, setGameResult] = useState<'win' | 'lose' | 'draw' | null>(null);
  const { updatePoints } = usePoints();
  const { addGameResult } = useGameHistory();

  const handleGameSelect = (index: number) => {
    if (!user) return;
    setSelectedGame(index);
  };

  const handleBetPlaced = (amount: number) => {
    if (!user || amount > user.points) return;
    
    updatePoints(user, -amount);
    setCurrentBet(amount);
    setIsPlaying(true);
    setGameResult(null);
  };

  const handleGameEnd = async (result: 'win' | 'lose' | 'draw') => {
    if (!user || selectedGame === null) return;

    const game = games[selectedGame];
    let pointsChange = 0;

    if (result === 'win') {
      pointsChange = currentBet * 2;
      updatePoints(user, pointsChange);
    } else if (result === 'draw') {
      pointsChange = currentBet;
      updatePoints(user, currentBet);
    }

    setGameResult(result);

    await addGameResult(
      user.username,
      user.walletAddress,
      game.type,
      currentBet,
      result
    );
  };

  const handlePlayAgain = () => {
    setCurrentBet(0);
    setIsPlaying(false);
    setGameResult(null);
  };

  const getGameDescription = (type: GameType) => {
    switch (type) {
      case 'chess':
        return "Challenge the court wizard in a game of chess. Use your strategic prowess to outmaneuver your opponent and claim victory in this timeless battle of minds.";
      case 'checkers':
        return "A classic game of strategy where you must capture your opponent's pieces while protecting your own. Plan your moves carefully and use tactical jumps to secure victory.";
      case 'connect4':
        return "Align four of your shields in a row - horizontally, vertically, or diagonally. Block your opponent while planning your path to victory in this tactical challenge.";
      case 'rockPaperScissors':
        return "Test your wit against the royal guard in this game of quick decisions. Choose between sword (rock), shield (paper), and bow (scissors) to outsmart your opponent.";
      case 'castleDefense':
        return "Defend your castle against waves of enemies! Balance between attacking the invaders and repairing your fortifications. Survive the siege to claim victory.";
      case 'ticTacToe':
        return "A simple yet strategic game played in taverns across the realm. Place your marks wisely to create a line of three and claim victory over your opponent.";
      default:
        return "";
    }
  };

  return (
    <div className="min-h-screen bg-amber-900 text-amber-100 p-8 bg-[url('https://images.unsplash.com/photo-1578662996442-48f60103fc96?auto=format&fit=crop&q=80')] bg-cover bg-center bg-blend-overlay">
      <div className="max-w-6xl mx-auto">
        <GameHeader onBack={onBack} user={user} />

        {selectedGame === null ? (
          <>
            <GameSelection user={user} onGameSelect={handleGameSelect} />
            <div className="mt-8">
              <LiveGameHistory />
            </div>
          </>
        ) : (
          <div className="space-y-6">
            <button
              onClick={() => setSelectedGame(null)}
              className="flex items-center gap-2 text-yellow-500 hover:text-yellow-400"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Games
            </button>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                {!isPlaying ? (
                  <div className="bg-gray-900 p-6 rounded-xl shadow-lg">
                    <h2 className="text-2xl font-bold text-white mb-4">{games[selectedGame].title}</h2>
                    <p className="text-gray-300 mb-6">{getGameDescription(games[selectedGame].type)}</p>
                    <div className="flex items-center gap-4 text-sm text-gray-400">
                      <div>{games[selectedGame].players}</div>
                      <div>Difficulty: {games[selectedGame].difficulty}</div>
                    </div>
                  </div>
                ) : (
                  <GameBoard
                    type={games[selectedGame].type}
                    difficulty={games[selectedGame].difficulty}
                    onWin={() => handleGameEnd('win')}
                    onLose={() => handleGameEnd('lose')}
                    onDraw={() => handleGameEnd('draw')}
                    isPlaying={isPlaying}
                  />
                )}
              </div>
              <div>
                <BettingPanel
                  user={user}
                  onBetPlaced={handleBetPlaced}
                  disabled={isPlaying}
                />
              </div>
            </div>
          </div>
        )}

        {gameResult && (
          <GameResult
            result={gameResult}
            betAmount={currentBet}
            onPlayAgain={handlePlayAgain}
          />
        )}
      </div>
    </div>
  );
}